/**
 * ゲームオーバー判定と、あふれ警告の計測（純粋ロジック / Phaser 非依存）。
 *
 * ゲームオーバーになるのは「器の外へこぼれて落ちた」ときだけ。
 * 口より上に積み上がっただけでは終了せず、`findViolatingPiece` +
 * `GameOverSystem`（latch: false）を「そろそろこぼれる」警告表示に使う。
 */

/** 判定対象となる料理の最小情報。 */
export interface OverLineCandidate {
  readonly id: number;
  readonly level: number;
  readonly x: number;
  readonly y: number;
  readonly radius: number;
  readonly createdAt: number;
  readonly isMerging: boolean;
  readonly alive: boolean;
}

export interface GameOverSystemOptions {
  thresholdMs: number;
  /** 生成直後の料理を判定対象外にする猶予時間。 */
  ignoreNewPieceMs: number;
  /** ラインをこの量以上越えていたら違反とみなす。 */
  lineMarginPx: number;
  /**
   * true（既定）: 一度成立したら以降は計測を止める（確定判定向け）。
   * false        : 条件が消えれば計測をやり直す（警告表示向け）。
   */
  latch?: boolean;
}

/** こぼれ判定に必要な器の情報。 */
export interface SpillBounds {
  /** 口（縁）の Y。これより下で器の外にいたらこぼれている。 */
  readonly rimY: number;
  /** 指定 Y における器の内側の半幅。 */
  halfWidthAt(y: number): number;
  readonly centerX: number;
  /** 器の外側と判定するまでの余白。 */
  readonly marginPx: number;
  /** この Y より下へ落ちたら確実にこぼれている。 */
  readonly fallOutY: number;
}

/**
 * ラインを越えている（＝ゲームオーバー候補の）料理があるかを判定する純粋関数。
 * ・落下直後（ignoreNewPieceMs 以内）の料理は無視する
 * ・合体処理中 / 破棄済みの料理は無視する
 */
export function findViolatingPiece(
  pieces: Iterable<OverLineCandidate>,
  lineY: number,
  now: number,
  options: GameOverSystemOptions,
): OverLineCandidate | null {
  for (const piece of pieces) {
    if (!piece.alive || piece.isMerging) continue;
    if (now - piece.createdAt < options.ignoreNewPieceMs) continue;
    // 料理の上端がラインより上（=y が小さい）なら違反
    if (piece.y - piece.radius <= lineY - options.lineMarginPx) {
      return piece;
    }
  }
  return null;
}

/**
 * 器の外へこぼれた料理を探す純粋関数。
 *
 * 器の外側には床が無いため、こぼれた料理はそのまま落下していく。
 * 「口より下にいるのに器の内側の幅より外にいる」または
 * 「画面下まで落ちた」のいずれかでこぼれと判定する。
 */
export function findSpilledPiece(
  pieces: Iterable<OverLineCandidate>,
  bounds: SpillBounds,
): OverLineCandidate | null {
  for (const piece of pieces) {
    if (!piece.alive || piece.isMerging) continue;
    if (piece.y >= bounds.fallOutY) return piece;
    if (piece.y <= bounds.rimY) continue;
    const limit = bounds.halfWidthAt(piece.y) + bounds.marginPx;
    if (Math.abs(piece.x - bounds.centerX) > limit) return piece;
  }
  return null;
}

export class GameOverSystem {
  private readonly options: GameOverSystemOptions;
  private elapsedMs = 0;
  private triggered = false;

  constructor(options: GameOverSystemOptions) {
    this.options = options;
  }

  get isTriggered(): boolean {
    return this.triggered;
  }

  /** 0〜1 の危険度（UI 演出用）。 */
  get progress(): number {
    if (this.options.thresholdMs <= 0) return 0;
    return Math.min(1, this.elapsedMs / this.options.thresholdMs);
  }

  get elapsed(): number {
    return this.elapsedMs;
  }

  /**
   * 経過時間を進める。violating が false になった時点で計測はリセットされる。
   * 閾値を超えた最初の呼び出しで true を返す。
   *
   * latch: false のときは条件が消えるたびに計測をやり直すので、
   * 「そろそろこぼれる」警告のように何度でも成立させられる。
   */
  update(deltaMs: number, violating: boolean): boolean {
    const latch = this.options.latch !== false;
    if (latch && this.triggered) return false;
    if (!violating) {
      this.elapsedMs = 0;
      if (!latch) this.triggered = false;
      return false;
    }
    this.elapsedMs += Math.max(0, deltaMs);
    if (this.elapsedMs >= this.options.thresholdMs && !this.triggered) {
      this.triggered = true;
      return true;
    }
    return false;
  }

  reset(): void {
    this.elapsedMs = 0;
    this.triggered = false;
  }
}
